<template>
  <Teleport to="body">
    <template v-if="visible">
      <div
        :class="['modal fade show d-block', modalClass]"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
        @mousedown.self="onBackdrop"
      >
        <div
          :class="[
            'modal-dialog',
            centered && 'modal-dialog-centered',
            scrollable && 'modal-dialog-scrollable',
            sizeClass,
            dialogClass
          ]"
          role="document"
        >
          <div :class="['modal-content', contentClass]">
            <div v-if="!hideHeader" :class="['modal-header', headerClass]">
              <slot name="modal-header" :close="hide">
                <h5 class="modal-title">{{ title }}</h5>
                <button type="button" class="close" aria-label="閉じる" @click="hide">
                  <span aria-hidden="true">&times;</span>
                </button>
              </slot>
            </div>
            <div :class="['modal-body', bodyClass]"><slot></slot></div>
            <div v-if="!hideFooter" :class="['modal-footer', footerClass]">
              <slot name="modal-footer" :ok="ok" :cancel="cancel" :hide="hide">
                <button type="button" class="btn btn-secondary" @click="cancel">キャンセル</button>
                <button type="button" class="btn btn-primary" @click="ok">OK</button>
              </slot>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-backdrop fade show"></div>
    </template>
  </Teleport>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  emitModalHidden,
  registerModal,
  unregisterModal,
  type ModalController
} from './modal-manager'

const openModals = new Set<symbol>()
const syncBodyState = () => document.body.classList.toggle('modal-open', openModals.size > 0)
export type BModalInstance = ModalController

export default defineComponent({
  name: 'BModal',
  props: {
    id: { type: String, default: '' },
    modelValue: { type: Boolean, default: false },
    title: { type: String, default: '' },
    centered: Boolean,
    scrollable: Boolean,
    hideHeader: Boolean,
    hideFooter: Boolean,
    noCloseOnBackdrop: Boolean,
    noCloseOnEsc: Boolean,
    modalClass: { type: [String, Array, Object], default: '' },
    dialogClass: { type: [String, Array, Object], default: '' },
    headerClass: { type: [String, Array, Object], default: '' },
    bodyClass: { type: [String, Array, Object], default: '' },
    contentClass: { type: [String, Array, Object], default: '' },
    footerClass: { type: [String, Array, Object], default: '' },
    size: { type: String, default: '' }
  },
  emits: ['show', 'shown', 'hide', 'hidden', 'ok', 'cancel', 'update:modelValue'],
  setup(props, { emit, expose }) {
    const visible = ref(false)
    const modalKey = Symbol('modal')
    const sizeClass = computed(() => (props.size ? `modal-${props.size}` : ''))
    const show = () => {
      if (visible.value) return
      emit('show')
      visible.value = true
      openModals.add(modalKey)
      syncBodyState()
      emit('update:modelValue', true)
      nextTick(() => emit('shown'))
    }
    const hide = () => {
      if (!visible.value) return
      emit('hide')
      visible.value = false
      openModals.delete(modalKey)
      syncBodyState()
      emit('update:modelValue', false)
      nextTick(() => {
        emit('hidden')
        emitModalHidden(props.id)
      })
    }
    const toggle = () => (visible.value ? hide() : show())
    const ok = () => { emit('ok'); hide() }
    const cancel = () => { emit('cancel'); hide() }
    const onBackdrop = () => { if (!props.noCloseOnBackdrop) cancel() }
    const onKeydown = (event: KeyboardEvent) => {
      if (visible.value && event.key === 'Escape' && !props.noCloseOnEsc) cancel()
    }
    const controller = { show, hide, toggle }
    watch(() => props.id, (id, oldId) => {
      unregisterModal(oldId, controller)
      registerModal(id, controller)
    })
    watch(() => props.modelValue, (value) => (value ? show() : hide()))
    onMounted(() => {
      registerModal(props.id, controller)
      window.addEventListener('keydown', onKeydown)
      if (props.modelValue) show()
    })
    onBeforeUnmount(() => {
      unregisterModal(props.id, controller)
      window.removeEventListener('keydown', onKeydown)
      openModals.delete(modalKey)
      syncBodyState()
    })
    expose(controller)
    return { visible, sizeClass, show, hide, ok, cancel, onBackdrop }
  }
})
</script>
